import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

const TagsStatistics = () => {
  const [tagCounts, setTagCounts] = useState({});
  const [totalLoads, setTotalLoads] = useState(0);
  const [selectedDay, setSelectedDay] = useState(
    new Date().toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' })
  );

  useEffect(() => {
    const fetchTagsData = async () => {
      // Reset states for new date selection
      setTagCounts({});
      setTotalLoads(0);

      try {
        // Correctly create date objects for the start and end of the day in Mexico City time
        // This avoids issues with the user's local browser timezone.
        const dayStartInMexicoCity = new Date(`${selectedDay}T00:00:00.000-06:00`); // CST
        const dayEndInMexicoCity = new Date(`${selectedDay}T23:59:59.999-06:00`); // CST
        
        const startTimestamp = Timestamp.fromDate(dayStartInMexicoCity);
        const endTimestamp = Timestamp.fromDate(dayEndInMexicoCity);

        const loadTimesRef = collection(db, 'loadTimes');
        const q = query(loadTimesRef, where('timestamp', '>=', startTimestamp), where('timestamp', '<=', endTimestamp));
        
        const [loadTimesSnapshot, tagsSnapshot] = await Promise.all([
          getDocs(q),
          getDocs(collection(db, 'tags')) // Fetch all tags once
        ]);

        const loadData = loadTimesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        if (loadData.length > 0) {
          processTagsData(loadData, tagsSnapshot);
        }
      } catch (error) {
        console.error('Error fetching tags or load times:', error);
      }
    };

    const processTagsData = (loadData, tagsSnapshot) => {
      const tagsMap = new Map(tagsSnapshot.docs.map(doc => [doc.id, doc.data().tags || []]));
      const dailyTagCounts = {};
      let loadCount = 0;
      const uniqueConversationIds = new Set();

      loadData.forEach(({ conversationId }) => {
        if (conversationId && !uniqueConversationIds.has(conversationId)) {
          uniqueConversationIds.add(conversationId);
          loadCount += 1;

          const tagsArray = tagsMap.get(conversationId) || [];

          if (tagsArray.includes('internal')) {
            dailyTagCounts.internal = (dailyTagCounts.internal || 0) + 1;
          } else {
            dailyTagCounts.external = (dailyTagCounts.external || 0) + 1;
          }

          tagsArray.forEach((tag) => {
            if (tag !== 'internal' && tag !== 'external') {
              dailyTagCounts[tag] = (dailyTagCounts[tag] || 0) + 1;
            }
          });
        }
      });

      setTagCounts(dailyTagCounts);
      setTotalLoads(loadCount);
    };

    fetchTagsData();
  }, [selectedDay]);

  return (
    <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-4xl mx-auto">
      <h3 className="text-2xl font-bold mb-6 text-gray-800 border-b pb-2">Tags Statistics</h3>

      {/* Date Selector */}
      <label className="block text-gray-700 text-md font-medium mb-2">Select Date:</label>
      <input
        type="date"
        className="block w-full p-3 border border-gray-300 rounded-md mb-6 focus:ring-2 focus:ring-blue-500 focus:outline-none"
        value={selectedDay}
        onChange={(e) => setSelectedDay(e.target.value)}
      />

      {/* Total Loads Display */}
      <div className="bg-blue-100 text-blue-800 text-xl font-semibold rounded-lg p-4 mb-6">
        <span>Total Loads on {selectedDay}:</span>
        <span className="font-bold ml-2">{totalLoads}</span>
      </div>

      {/* Tags Count and Percentage Display */}
      <h4 className="text-xl font-semibold mb-4 text-gray-800">Tag Counts and Percentages</h4>
      <ul className="space-y-3 bg-gray-50 rounded-lg p-5 shadow-inner">
        {Object.keys(tagCounts).length > 0 ? (
          Object.entries(tagCounts).map(([tag, count]) => {
            const percentage = totalLoads > 0 ? ((count / totalLoads) * 100).toFixed(2) : 0;
            return (
              <li key={tag} className="flex justify-between items-center p-3 bg-white rounded-md shadow-sm">
                <span className="text-gray-700 font-medium capitalize">{tag}</span>
                <span className="text-gray-600">
                  <span className="font-semibold">{count}</span>
                  <span className="text-sm text-gray-500 ml-1">({percentage}%)</span>
                </span>
              </li>
            );
          })
        ) : (
          <li className="text-gray-500">No tags found for the selected date.</li>
        )}
      </ul>
    </div>
  );
};

export default TagsStatistics;
